<template>
  <div class="models-form default-form">
    <ValidationObserver ref="form">
      <div class="columns">
        <div class="column">
          <div class="row">
            <validation-provider name="Название" rules="required">
              <template #default="{ errors }">
                <label>Название</label>
                <input
                  type="text"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  v-model="model.title"
                  placeholder="Название"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Статус" rules="required">
              <template #default="{ errors }">
                <label>Статус</label>
                <multiselect
                  v-model="model.status"
                  :options="statuses"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  track-by="id"
                  label="title"
                  placeholder="Статус"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>
        </div>

        <div class="column img">
          <div class="row">
            <validation-provider name="Изображение">
              <template #default="{ errors }">
                <label>Изображение</label>
                <vue-dropzone
                  id="dropzone"
                  ref="imageDropzone"
                  v-model="model.image_url"
                  :options="dropzoneOptions"
                  :destroyDropzone="false"
                  :class="{ 'form-error': errors[0]}"
                  @vdropzone-success="(file, response) => success(file, response, 'imageDropzone')"
                  @vdropzone-mounted="() => dropzoneMounted('imageDropzone')"
                  @vdropzone-removed-file="(file) => dropzoneFileRemoved(file, 'imageDropzone')"
                  @vdropzone-file-added="(file) => dropzoneAdded(file, 'imageDropzone')"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Файл руководства">
              <template #default="{ errors }">
                <label>Файлы руководств</label>
                <vue-dropzone
                  id="fileDropzone"
                  ref="fileDropzone"
                  v-model="model.manual_urls"
                  :options="fileDropzoneOptions"
                  :destroyDropzone="false"
                  :class="{ 'form-error': errors[0], 'max-exceeded': maxFilesExceeded, 'allowFilename': true}"
                  @vdropzone-success="(file, response) => success(file, response, 'fileDropzone')"
                  @vdropzone-mounted="() => dropzoneMounted('fileDropzone')"
                  @vdropzone-removed-file="(file) => dropzoneFileRemoved(file, 'fileDropzone')"
                  @vdropzone-file-added="(file) => dropzoneAdded(file, 'fileDropzone')"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>
        </div>
      </div>

      <div class="row">
        <label>Комментарий</label>
        <textarea v-model="model.comment" :disabled="readOnly" rows="10" placeholder="Комментарий" />
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone';

import { mapActions, mapGetters } from 'vuex';

import options from '@/config/dropzone-options';

import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  name: 'models-form',

  components: {
    vueDropzone: vue2Dropzone,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    model: Object,
  },

  data() {
    return {
      maxFiles: 10,
    };
  },

  computed: {
    ...mapGetters({
      statuses: 'models/statuses',
      uploadResponse: 'util/uploadResponse',
    }),

    dropzoneOptions() {
      const STATIC_URL = process.env.VUE_APP_STATIC_URL;
      const BRAND_PREFIX = process.env.VUE_APP_BRAND_PREFIX;
      const url = `c/${BRAND_PREFIX}`;

      return {
        ...options,
        url,
        addRemoveLinks: true,
      };
    },

    fileDropzoneOptions() {
      return {
        ...this.dropzoneOptions,
        maxFiles: this.maxFiles,
        maxFilesize: 100,
        dictDefaultMessage: 'Нажмите, чтобы добавить файл',
        dictRemoveFile: 'Удалить файл',
        dictFileTooBig:
          'Файл слишком большой: {{filesize}} МБ. Максимальный размер файла {{maxFilesize}} МБ',
      };
    },

    readOnly() {
      return !!this.model.deleted_at;
    },

    maxFilesExceeded() {
      return this.model.manual_urls
        ? this.model.manual_urls.length > this.maxFiles
        : false;
    },
  },

  methods: {
    ...mapActions({
      uploadStaticFile: 'util/uploadStaticFile',
      removeStaticFile: 'util/removeStaticFile',
    }),

    async dropzoneAdded(file, dropzone) {
      if (!file.manuallyAdded) {
        const formData = new FormData();
        formData.append('file', file);

        await this.uploadStaticFile({
          formData,
          listener: (event, file) => this.updateProgress(event, file, dropzone),
          file,
        });

        file.url = this.getUrlByFileName(this.uploadResponse.data.data.name);

        this.$refs[dropzone].dropzone
          .emit('complete', file)
          .emit('success', file, this.uploadResponse);
      }
    },

    updateProgress(event, file, dropzone) {
      const percentCompleted = Math.floor((event.loaded * 100) / event.total);

      this.$refs[dropzone].dropzone.emit(
        'uploadprogress',
        file,
        percentCompleted
      );
    },

    success(file, response, dropzone) {
      const url = process.env.VUE_APP_CDN_STATIC_URL + response.data.data.name;
      this.$emit(
        dropzone === 'imageDropzone' ? 'imageUploaded' : 'fileUploaded',
        url
      );
    },

    async validate() {
      return await this.$refs.form.validate();
    },

    async dropzoneFileRemoved(file, dropzone) {
      const url = this.extractUrl(file);
      if (url) {
        await this.removeStaticFile(url);
        this.$emit(
          dropzone === 'imageDropzone' ? 'imageDeleted' : 'fileDeleted',
          url
        );
      } else {
        console.warn('No image to delete');
      }
    },

    extractUrl(file) {
      if (file.url) {
        return file.url;
      } else {
        if (file.xhr) {
          if (file.xhr.response) {
            const response = JSON.parse(file.xhr.response);
            return response.data
              ? this.getUrlByFileName(response.data.name)
              : false;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }
    },

    getUrlByFileName(name) {
      return process.env.VUE_APP_STATIC_URL + name;
    },

    dropzoneMounted(dropzone) {
      if (dropzone === 'imageDropzone') {
        if (this.model.image_url) {
          this.addFileByUrl(this.model.image_url, dropzone);
        }
      } else {
        if (this.model.manual_urls) {
          this.model.manual_urls.forEach(url => {
            this.addFileByUrl(url, dropzone);
          });
        }
      }
    },

    addFileByUrl(url, dropzone) {
      this.$refs[dropzone].manuallyAddFile(
        {
          url,
          size: null,
          name: url,
        },
        url
      );
    },
  },
};
</script>
